"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SignalServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
const rxjs_1 = require("rxjs");
/**
 * SignalService for managing RxJS signals throughout the application
 * Provides a centralized way to create, manage, and clean up reactive signals
 */
class SignalService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.signals = new Map();
        this.subscriptions = new Map();
        this.destroySubject = new rxjs_1.Subject();
    }
    /**
     * Create a new signal with optional configuration
     */
    createSignal(key, config = {}) {
        if (this.signals.has(key)) {
            // Return existing signal with the same logic as getSignal
            const existingSubject = this.signals.get(key);
            let value$ = existingSubject.asObservable().pipe((0, rxjs_1.distinctUntilChanged)(), (0, rxjs_1.shareReplay)({ bufferSize: 1, refCount: true }), (0, rxjs_1.takeUntil)(this.destroySubject));
            return {
                value$,
                setValue: (value) => {
                    if (!existingSubject.closed) {
                        existingSubject.next(value);
                    }
                },
                getValue: () => existingSubject.value,
                destroy: () => this.destroySignal(key)
            };
        }
        const subject = new rxjs_1.BehaviorSubject(config.initialValue);
        this.signals.set(key, subject);
        this.subscriptions.set(key, []);
        let value$ = subject.asObservable();
        // Apply configuration options
        if (config.distinctUntilChanged !== false) {
            value$ = value$.pipe((0, rxjs_1.distinctUntilChanged)());
        }
        if (config.shareReplay !== false) {
            value$ = value$.pipe((0, rxjs_1.shareReplay)({
                bufferSize: config.bufferSize || 1,
                refCount: true
            }));
        }
        // Auto-cleanup on service destroy
        value$ = value$.pipe((0, rxjs_1.takeUntil)(this.destroySubject));
        return {
            value$,
            setValue: (value) => {
                if (!subject.closed) {
                    subject.next(value);
                }
            },
            getValue: () => subject.value,
            destroy: () => this.destroySignal(key)
        };
    }
    /**
     * Get an existing signal by key, or create it if it doesn't exist
     */
    getSignal(key, config = {}) {
        const existingSubject = this.signals.get(key);
        if (existingSubject) {
            let value$ = existingSubject.asObservable().pipe((0, rxjs_1.distinctUntilChanged)(), (0, rxjs_1.shareReplay)({ bufferSize: 1, refCount: true }), (0, rxjs_1.takeUntil)(this.destroySubject));
            return {
                value$,
                setValue: (value) => {
                    if (!existingSubject.closed) {
                        existingSubject.next(value);
                    }
                },
                getValue: () => existingSubject.value,
                destroy: () => this.destroySignal(key)
            };
        }
        // Signal doesn't exist, create it
        return this.createSignal(key, config);
    }
    /**
     * Check if a signal exists
     */
    hasSignal(key) {
        return this.signals.has(key);
    }
    /**
     * Set value for an existing signal
     */
    setSignalValue(key, value) {
        const subject = this.signals.get(key);
        if (subject && !subject.closed) {
            subject.next(value);
            return true;
        }
        return false;
    }
    /**
     * Get current value of a signal
     */
    getSignalValue(key) {
        const subject = this.signals.get(key);
        return subject ? subject.value : undefined;
    }
    /**
     * Create a computed signal based on other signals
     */
    createComputedSignal(key, sourceKeys, computeFn, config = {}) {
        if (this.signals.has(key)) {
            console.warn(`Computed signal already exists: '${key}'. Returning existing signal.`);
            return this.getSignal(key);
        }
        const sourceSignals = sourceKeys.map(sourceKey => {
            const signal = this.getSignal(sourceKey);
            if (!signal) {
                throw new Error(`Source signal not found: '${sourceKey}' for computed signal '${key}'`);
            }
            return signal.value$;
        });
        // Combine all source signals and compute the result
        const computedSubject = new rxjs_1.BehaviorSubject(config.initialValue);
        this.signals.set(key, computedSubject);
        this.subscriptions.set(key, []);
        // Subscribe to changes in source signals
        const subscription = this.combineLatest(sourceSignals).subscribe(values => {
            try {
                const result = computeFn(...values);
                if (!computedSubject.closed) {
                    computedSubject.next(result);
                }
            }
            catch (error) {
                console.error(`Computation error in signal '${key}':`, error);
            }
        });
        this.addSubscription(key, subscription);
        let value$ = computedSubject.asObservable();
        if (config.distinctUntilChanged !== false) {
            value$ = value$.pipe((0, rxjs_1.distinctUntilChanged)());
        }
        if (config.shareReplay !== false) {
            value$ = value$.pipe((0, rxjs_1.shareReplay)({
                bufferSize: config.bufferSize || 1,
                refCount: true
            }));
        }
        value$ = value$.pipe((0, rxjs_1.takeUntil)(this.destroySubject));
        return {
            value$,
            setValue: () => {
                console.warn(`Cannot set value on computed signal '${key}'. Computed signals are read-only.`);
            },
            getValue: () => computedSubject.value,
            destroy: () => this.destroySignal(key)
        };
    }
    /**
     * Create a filtered signal that only emits when a condition is met
     */
    createFilteredSignal(key, sourceKey, filterFn, config = {}) {
        const sourceSignal = this.getSignal(sourceKey);
        if (!sourceSignal) {
            throw new Error(`Source signal not found: '${sourceKey}' for filtered signal '${key}'`);
        }
        const filteredSubject = new rxjs_1.BehaviorSubject(config.initialValue);
        this.signals.set(key, filteredSubject);
        this.subscriptions.set(key, []);
        const subscription = sourceSignal.value$
            .pipe((0, rxjs_1.filter)(filterFn))
            .subscribe(value => {
            if (!filteredSubject.closed) {
                filteredSubject.next(value);
            }
        });
        this.addSubscription(key, subscription);
        let value$ = filteredSubject.asObservable();
        if (config.distinctUntilChanged !== false) {
            value$ = value$.pipe((0, rxjs_1.distinctUntilChanged)());
        }
        if (config.shareReplay !== false) {
            value$ = value$.pipe((0, rxjs_1.shareReplay)({
                bufferSize: config.bufferSize || 1,
                refCount: true
            }));
        }
        value$ = value$.pipe((0, rxjs_1.takeUntil)(this.destroySubject));
        return {
            value$,
            setValue: (value) => {
                // Update the source signal instead
                sourceSignal.setValue(value);
            },
            getValue: () => filteredSubject.value,
            destroy: () => this.destroySignal(key)
        };
    }
    /**
     * Add a subscription to track for a signal
     */
    addSubscription(key, subscription) {
        const subscriptions = this.subscriptions.get(key) || [];
        subscriptions.push(subscription);
        this.subscriptions.set(key, subscriptions);
    }
    /**
     * Simple combineLatest implementation
     */
    combineLatest(observables) {
        return new rxjs_1.Observable(subscriber => {
            const values = new Array(observables.length);
            const hasValue = new Array(observables.length).fill(false);
            let completed = 0;
            const subscriptions = observables.map((obs, index) => {
                return obs.subscribe({
                    next: (value) => {
                        values[index] = value;
                        hasValue[index] = true;
                        if (hasValue.every(has => has)) {
                            subscriber.next([...values]);
                        }
                    },
                    error: (error) => subscriber.error(error),
                    complete: () => {
                        completed++;
                        if (completed === observables.length) {
                            subscriber.complete();
                        }
                    }
                });
            });
            return () => {
                subscriptions.forEach(sub => sub.unsubscribe());
            };
        });
    }
    /**
     * Destroy a specific signal and clean up its subscriptions
     */
    destroySignal(key) {
        const subject = this.signals.get(key);
        const subscriptions = this.subscriptions.get(key) || [];
        // Unsubscribe all related subscriptions
        subscriptions.forEach(sub => {
            if (!sub.closed) {
                sub.unsubscribe();
            }
        });
        // Complete and remove the subject
        if (subject && !subject.closed) {
            subject.complete();
        }
        this.signals.delete(key);
        this.subscriptions.delete(key);
    }
    /**
     * Get all signal keys
     */
    getSignalKeys() {
        return Array.from(this.signals.keys());
    }
    /**
     * Clear all signals and subscriptions
     */
    clearAllSignals() {
        this.signals.forEach((subject, key) => {
            this.destroySignal(key);
        });
        this.signals.clear();
        this.subscriptions.clear();
    }
    /**
     * Get signal status information for debugging
     */
    getSignalStatus(key) {
        if (!this.signals.has(key)) {
            return null;
        }
        const subject = this.signals.get(key);
        const subscriptions = this.subscriptions.get(key) || [];
        return {
            exists: true,
            closed: subject.closed,
            hasSubscriptions: subscriptions.length > 0,
            subscriptionCount: subscriptions.length
        };
    }
    /**
     * Service cleanup - called when service is destroyed
     */
    destroy() {
        this.destroySubject.next();
        this.destroySubject.complete();
        this.clearAllSignals();
    }
}
exports.SignalServiceInstance = SignalService;
exports.default = SignalService.getSingleton();
